/**
 * Shared deck launch routing helpers.
 * Used by My Library, Global Library, Folder View, Teacher Library.
 * Ensures consistent mode routing and CTA labels across the app.
 */
import type { TFunction } from "i18next";

export type DeckLaunchMode = "cards" | "learn" | "test" | "games";

/** Get the route path for launching study (for owned decks). */
export function getDeckLaunchPath(params: {
  deckId: string;
  mode: DeckLaunchMode;
  basePath?: string;
  fullscreen?: boolean;
  isMobile?: boolean;
}): string {
  const { deckId, mode, basePath = "", fullscreen, isMobile } = params;
  const prefix = basePath || "/study";
  const fullscreenSuffix =
    fullscreen && isMobile
      ? `?mode=fullscreen${mode === "learn" ? "&type=quiz" : mode === "test" ? "&type=test" : ""}`
      : !fullscreen && (mode === "learn" || mode === "test")
        ? mode === "learn"
          ? "?type=quiz"
          : "?type=test"
        : "";

  if (mode === "games") {
    return `${prefix}/${deckId}${fullscreenSuffix}`;
  }
  return `${prefix}/${deckId}${fullscreenSuffix}`;
}

/** Get the CTA label key for the given mode (for use with t()). */
export function getDeckLaunchLabelKey(mode: DeckLaunchMode, deckType?: string): string {
  if (deckType === "tests") return "deckDashboard.startTest";
  switch (mode) {
    case "cards":
      return "deckDashboard.startCards";
    case "learn":
      return "deckDashboard.startLearn";
    case "test":
      return "deckDashboard.startTest";
    case "games":
      return "home.play";
    default:
      return "deckDashboard.startCards";
  }
}

/** Get the translated CTA label for the given mode. */
export function getDeckLaunchLabel(t: TFunction, mode: DeckLaunchMode, deckType?: string): string {
  return t(getDeckLaunchLabelKey(mode, deckType));
}

/** Map filter/tab value to DeckLaunchMode. */
export function filterToLaunchMode(filter: string | null): DeckLaunchMode {
  if (filter === "learn") return "learn";
  if (filter === "test") return "test";
  if (filter === "match") return "games";
  return "cards";
}
